import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import ScenarioSwitcher from './ScenarioSwitcher';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import { ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import { CostGroup } from '../../../models/CostGroup';
import { CostItem } from '../../../models/CostItem';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import { UserRoles } from '../../../constants/UserRoles';
import { FormFieldConfig } from '../../common/FormGenerator/FormGenerator';
import './ProjectCosts.scss'; // Assuming you have styles for this component
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { useTranslation } from 'react-i18next';

const generateGuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });

const ProjectCosts: React.FC<{
  scenarioId: string;
  setScenarioId: (id: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ scenarioId, setScenarioId, onDirtyChange }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios, setScenarios } = useProjectScenarios();

  const costItemColumns: DetailedListColumn<CostItem>[] = React.useMemo(() => [
    { key: 'name', header: t('common:name'), dataField: 'name', type: 'string' },
    { key: 'cost', header: t('screens:projectPricing.cost'), dataField: 'cost', type: 'number' },
    { key: 'multiplier', header: t('screens:projectPricing.multiplier'), dataField: 'multiplier', type: 'number', defaultValue: 1 },
    {
      key: 'total',
      header: t('common:total'),
      render: (item: CostItem) => {
        const cost = Number(item.cost) || 0;
        const multiplier = Number(item.multiplier) || 0;
        return (cost * multiplier).toLocaleString(undefined, { maximumFractionDigits: 2 });
      },
    },
  ], [t]);

  const costItemFormFields: FormFieldConfig[] = React.useMemo(() => [
    { key: 'name', label: t('common:name'), type: 'string', required: true },
    { key: 'cost', label: t('screens:projectPricing.cost'), type: 'number', required: true, min: 0 },
    { key: 'multiplier', label: t('screens:projectPricing.multiplier'), type: 'number', required: true, min: 0 },
  ], [t]);
  const [scenario, setScenario] = useState(() => scenarios.find(s => s.id === scenarioId) || scenarios[0]);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<{ [name: string]: boolean }>({});
  const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  // Group edit state: { [groupIdx]: { name: string, editing: boolean } }
  const [groupEditStates, setGroupEditStates] = useState<{ [idx: number]: { name: string; editing: boolean } }>({});
  const provider = React.useRef(new ProjectsScenariosRestDataProvider()).current;

  useEffect(() => {
    setScenario(scenarios.find(s => s.id === scenarioId) || scenarios[0]);
  }, [scenarios, scenarioId]);

  // Autosave when switching away from this tab/component or scenarioId changes
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);

    return () => {
      // Only autosave if dirty when unmounting or scenarioId changes
      if (isDirty) {
        // Call handleSave synchronously and block navigation until save completes
        // (React will not block navigation, but we can at least ensure save is awaited)
        // eslint-disable-next-line no-void
        void handleSave();
      }
    };
  }, [scenarioId, isDirty]);

  if (!scenario) return <div className="card-glass project-tab-section"><h4 className="section-title">{t('screens:projectCosts.costGroups')}</h4>{t('screens:projects.noScenarioSelected')}</div>;

  const costGroups: CostGroup[] = scenario && scenario.costGroups ? scenario.costGroups : [];


  // --- Cost Group Management (MATCH ProjectEstimate) ---
  // Drag-and-drop handler for cost groups
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setScenarios(prev => prev.map(s => {
      if (s.id !== scenario.id) return s;
      const groups = s.costGroups ? [...s.costGroups] : [];
      const [removed] = groups.splice(from, 1);
      groups.splice(to, 0, removed);
      return { ...s, costGroups: groups };
    }));
    setIsDirty(true);
  };

  const handleAddGroup = () => {
    const name = newGroupName.trim();
    if (!name) return;
    setScenarios(prev => prev.map(s => {
      if (s.id !== scenario.id) return s;
      const groups = s.costGroups ? [...s.costGroups] : [];
      const newGroup: CostGroup = { name, items: [] };
      return { ...s, costGroups: [...groups, newGroup] };
    }));
    setNewGroupName('');
    setAddGroupDialogOpen(false);
    setIsDirty(true);
  };

  const handleDeleteGroup = (idx: number) => {
    setScenarios(prev => prev.map(s => {
      if (s.id !== scenario.id) return s;
      const groups = s.costGroups ? [...s.costGroups] : [];
      groups.splice(idx, 1);
      return { ...s, costGroups: groups };
    }));
    setGroupToDelete(null);
    setIsDirty(true);
  };

  const handleGroupNameChange = (idx: number, name: string) => {
    setGroupEditStates(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), name } }));
  };

  const handleGroupEdit = (idx: number) => {
    const group = costGroups[idx];
    setGroupEditStates(prev => ({ ...prev, [idx]: { name: group?.name || '', editing: true } }));
  };

  const handleGroupEditSave = (idx: number) => {
    const newName = groupEditStates[idx]?.name.trim();
    if (!newName) return;
    setScenarios(prev => prev.map(s => {
      if (s.id !== scenario.id) return s;
      const groups = s.costGroups ? [...s.costGroups] : [];
      groups[idx] = { ...groups[idx], name: newName };
      return { ...s, costGroups: groups };
    }));
    setGroupEditStates(prev => ({ ...prev, [idx]: { name: newName, editing: false } }));
    setIsDirty(true);
  };

  const handleGroupEditCancel = (idx: number) => {
    setGroupEditStates(prev => ({ ...prev, [idx]: { ...prev[idx], editing: false } }));
  };

  const moveGroup = (idx: number, direction: -1 | 1) => {
    setScenarios(prev => prev.map(s => {
      if (s.id !== scenario.id) return s;
      const groups = s.costGroups ? [...s.costGroups] : [];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= groups.length) return s;
      const [removed] = groups.splice(idx, 1);
      groups.splice(newIdx, 0, removed);
      return { ...s, costGroups: groups };
    }));
    setIsDirty(true);
  };

  // --- Cost Item Management (MATCH ProjectEstimate) ---
  const handleItemsChange = (groupIdx: number, newItems: CostItem[]) => {
    setScenarios(prev => prev.map(s => {
      if (s.id !== scenario.id) return s;
      const groups = s.costGroups ? [...s.costGroups] : [];
      const group = { ...groups[groupIdx] };
      group.items = newItems.map(item => ({ ...item, id: item.id || generateGuid() }));
      groups[groupIdx] = group;
      return { ...s, costGroups: groups };
    }));
    setIsDirty(true);
  };

  // Save handler for ScenarioSwitcher (MATCH ProjectEstimate)
  const handleSave = async () => {
    // Always use the latest scenario from scenarios array
    const latestScenario = scenarios.find(s => s.id === scenario.id);
    if (!latestScenario?.id) return;
    setSaving(true);
    try {
      await provider.update(latestScenario.id, latestScenario);
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <ScenarioSwitcher scenarioId={scenario?.id} setScenarioId={setScenarioId} onSave={handleSave} saving={saving} />

        <div className="card-glass project-tab-section">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <h4 className="section-title" style={{ margin: 0, flex: 1 }}>{t('screens:projectCosts.costGroups')}</h4>
            <button className="btn btn-primary" onClick={() => setAddGroupDialogOpen(true)}>＋</button>
          </div>
            <InfoPanel header="Project Costs">
            This section allows you to manage cost groups and items for the project. You can add, edit, and delete cost groups, as well as manage individual cost items within each group. The total costs will be calculated based on the items and their multipliers.
            These form the BOM associated with the project excluding effort/labor, such as materials, equipment, and other non-labor expenses.
            </InfoPanel>

          {costGroups.length === 0 && <div style={{paddingLeft:6}}>{t('screens:projectCosts.noCostGroups')}</div>}
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="costgroups-droppable">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {costGroups.map((group, groupIdx) => (
                    <Draggable key={groupIdx} draggableId={group.name + '-' + groupIdx} index={groupIdx}>
                      {(dragProvided, dragSnapshot) => ( 
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className="phase-accordion-section"
                          style={{
                            ...dragProvided.draggableProps.style,
                            marginBottom: 8,
                            background: dragSnapshot.isDragging ? '#f3f0fa' : undefined,
                            borderRadius: 8,
                            boxShadow: dragSnapshot.isDragging ? '0 2px 8px rgba(123,63,228,0.10)' : undefined,
                          }}
                        >
                          <div
                            className="phase-header phase-header-hoverable"
                            style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', width: '100%', position: 'relative', minHeight: 44 }}
                            onClick={e => {
                              if ((e.target as HTMLElement).closest('.phase-header-btns') || (e.target as HTMLElement).closest('.phase-drag-handle')) return;
                              setExpandedGroups(prev => ({ ...prev, [groupIdx]: !prev[groupIdx] }));
                            }}
                          >
                            <span
                              {...dragProvided.dragHandleProps}
                              className="phase-drag-handle"
                              style={{ cursor: 'grab', marginRight: 8, color: '#bbb', fontSize: 18, userSelect: 'none' }}
                              onClick={e => e.stopPropagation()}
                            >☰</span>
                            {groupEditStates[groupIdx]?.editing ? (
                              <>
                                <input
                                  type="text"
                                  className="phase-edit-input"
                                  value={groupEditStates[groupIdx]?.name}
                                  onChange={e => handleGroupNameChange(groupIdx, e.target.value)}
                                  onBlur={() => handleGroupEditSave(groupIdx)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') handleGroupEditSave(groupIdx);
                                    if (e.key === 'Escape') handleGroupEditCancel(groupIdx);
                                  }}
                                  autoFocus
                                  style={{ fontWeight: 800, fontSize: 16, marginRight: 8 }}
                                  onClick={e => e.stopPropagation()}
                                />
                                <span style={{ flex: 1 }} />
                                <div className="phase-header-btns phase-header-btns-visible" style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                                  <button onClick={e => { e.stopPropagation(); handleGroupEditSave(groupIdx); }}>✔</button>
                                  <button onClick={e => { e.stopPropagation(); handleGroupEditCancel(groupIdx); }}>✖</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span style={{ fontWeight: 800, fontSize: 16 }}>{group.name}</span>
                                <span style={{ flex: 1 }} />
                                <div className="phase-header-btns" style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                                  <button onClick={e => { e.stopPropagation(); handleGroupEdit(groupIdx); }} title="Rename">✎</button>
                                  <button onClick={e => { e.stopPropagation(); moveGroup(groupIdx, -1); }} disabled={groupIdx === 0} title="Move up">↑</button>
                                  <button onClick={e => { e.stopPropagation(); moveGroup(groupIdx, 1); }} disabled={groupIdx === costGroups.length - 1} title="Move down">↓</button>
                                  <button onClick={e => { e.stopPropagation(); setGroupToDelete(groupIdx.toString()); }} title="Delete" style={{ color: '#c00' }}>🗑</button>
                                </div>
                              </>
                            )}
                            <button
                              onClick={e => { e.stopPropagation(); setExpandedGroups(prev => ({ ...prev, [groupIdx]: !prev[groupIdx] })); }}
                              style={{ marginLeft: 8, background: 'none', border: 'none', color: '#bbb', fontSize: 18, cursor: 'pointer', padding: 0 }}
                              title={expandedGroups[groupIdx] ? 'Collapse' : 'Expand'}
                              tabIndex={-1}
                              aria-label={expandedGroups[groupIdx] ? 'Collapse section' : 'Expand section'}
                            >
                              {expandedGroups[groupIdx] ? '▾' : '▸'}
                            </button>
                          </div>
                          {expandedGroups[groupIdx] && (
                            <div>
                              <DetailedListComponent
                                dataSource={{
                                  data: (group.items || []).map(item => ({ ...item, id: item.id || generateGuid() })),
                                  setData: (data: CostItem[]) => handleItemsChange(groupIdx, data)
                                }}
                                columns={costItemColumns}
                                rowKey={(item: any) => item.id}
                                addRole={UserRoles.Admin + ", " + UserRoles.Manager}
                                editRole={UserRoles.Admin + ", " + UserRoles.Manager}
                                deleteRole={UserRoles.Admin + ", " + UserRoles.Manager}
                                formFields={costItemFormFields}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>


      {/* Add Group Dialog */}
      <PopupDialog
        open={addGroupDialogOpen}
        title={t('screens:projectCosts.addNewCostGroup')}
        onClose={() => setAddGroupDialogOpen(false)}
        actions={[
          <button key="cancel" onClick={() => setAddGroupDialogOpen(false)}>{t('common:cancel')}</button>,
          <button key="add" onClick={handleAddGroup} disabled={!newGroupName.trim()} style={{ fontWeight: 600, color: '#6610f2' }}>{t('common:add')}</button>
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 260 }}>
          <label htmlFor="new-group-name" style={{ fontWeight: 500, marginBottom: 4 }}>{t('screens:projectCosts.groupName')}</label>
          <input
            id="new-group-name"
            type="text"
            className="form-control"
            placeholder={t('screens:projectCosts.enterGroupName')}
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newGroupName.trim()) handleAddGroup();
            }}
            autoFocus
          />
        </div>
      </PopupDialog>

      {/* Delete Group Dialog */}
      <PopupDialog
        open={!!groupToDelete}
        title={t('screens:projectCosts.deleteCostGroup')}
        onClose={() => setGroupToDelete(null)}
        actions={[
          <button key="cancel" onClick={() => setGroupToDelete(null)}>{t('common:cancel')}</button>,
          <button key="delete" onClick={() => handleDeleteGroup(Number(groupToDelete))} style={{ color: '#c00', fontWeight: 600 }}>{t('common:delete')}</button>
        ]}
      >
        {t('screens:projectCosts.confirmDeleteCostGroup')}
      </PopupDialog>
    </div>
  );
};

export default ProjectCosts;